import CameraIcon from '@mui/icons-material/CameraAlt';
import { Box } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import Routes, { CHECK_IN_TRAY_ID_PARAM, DELIVERY_ID_PARAM, SET_ID_PARAM } from 'routes';
import { useDelivery } from 'api/hooks/useDelivery';
import { useValidateTrayReference } from 'api/hooks/useValidateTrayReference';
import Button from 'components/Button';
import Page from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import SavingPleaseWaitDialog from 'components/SavingPleaseWaitDialog';
import { useLanguage } from 'context/AppContext';
import { useCheckInRedirect } from 'hooks/useCheckInRedirect';
import { CheckInSet, CheckInTraysBySet, useCheckInTraysBySet } from 'hooks/useCheckInTraysBySet';
import { useSaveAndCompleteCheckIn } from 'hooks/useSaveAndCompleteCheckIn';
import { CheckInTrayDetails } from './components/CheckInTrayDetails';
import { ConfirmDecantTrayDialog } from './components/ConfirmDecantTrayDialog';
import useSetDisplayLabel, { getSetDisplayLabel } from 'hooks/useSetDisplayLabel';
import { useTrayBarcodeAssignmentSetting } from 'api/hooks/useTrayBarcodeAssignmentSetting';
import { CheckInTrayDialogs, VisibleDialog } from 'components/CheckInTrayDialogs';

export const CheckInTrayDetailsPage: FC = () => {
  const langAll = useLanguage()[0];
  const lang = langAll.CheckInTrayDetailsPage;
  const { deliveryId = '', setId: setKitInstanceId = '', checkInTrayId = '' } = useParams();

  const { data: delivery } = useDelivery(deliveryId);
  const [checkInTraysBySet, setCheckInTraysBySet] = useCheckInTraysBySet();

  const [allDevicesCleanOfDebris, setAllDevicesCleanOfDebris] = useState(true);
  const [allDevicesFunctional, setAllDevicesFunctional] = useState(true);
  const [allDevicesInTray, setAllDevicesInTray] = useState(true);
  const [missingPartsComment, setMissingPartsComment] = useState('');
  const [comments, setComments] = useState('');

  const [visibleDialog, setVisibleDialog] = useState<VisibleDialog | null>(null);

  const [isSaving, setIsSaving] = useState(false);

  const { mutateAsync: validateTrayReference } = useValidateTrayReference();
  const { saveAndComplete, isLoading: isSavingAndCompleting } = useSaveAndCompleteCheckIn();

  const updateMissingPartsComment = useCallback((comment: string) => {
    setMissingPartsComment(comment);
  }, []);

  const navigate = useNavigate();

  const deliverySet = useMemo(
    () => delivery?.sets.find(set => set.kitInstanceId === setKitInstanceId),
    [delivery, setKitInstanceId],
  );
  const setName = useSetDisplayLabel(deliverySet);

  const consolidatedPrefill = useMemo(() => {
    if (!deliverySet?.consolidatedSet) return '';
    const names = (delivery?.sets || [])
      .filter(s => s.consolidatedIntoKitInstanceId == deliverySet.kitInstanceId)
      .map(s => getSetDisplayLabel(s))
      .join(', ');
    return names ? langAll.SelectSetDialog.commentsPrefill({ names }) : '';
  }, [delivery?.sets, deliverySet, langAll.SelectSetDialog]);

  useEffect(() => {
    if (consolidatedPrefill) setComments(current => current || consolidatedPrefill);
  }, [consolidatedPrefill]);
  const set = checkInTraysBySet[setKitInstanceId];
  const tray = set?.trays[checkInTrayId];
  const trayName = set?.trays[checkInTrayId]?.name || '';
  const pageTitle = lang.title({
    setName,
    trayName,
    referenceNumber: '',
  });

  useCheckInRedirect({
    deliveryId,
    setKitInstanceId,
    checkInTrayId,
    disabled: isSaving || !!visibleDialog || isSavingAndCompleting,
  });

  const { generateTrayBarcode } = useTrayBarcodeAssignmentSetting();

  useEffect(() => {
    // if the set or tray is not in the checkInTraysBySet, then the user has navigated to the page directly
    // and we should navigate them back to the check in page
    if (!set || !tray) {
      navigate(Routes.CheckInWorkflow.path.replace(DELIVERY_ID_PARAM, deliveryId));
    }
  }, [deliveryId, navigate, set, tray]);

  const onBackClick = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const updateCheckInTraysBySet = useCallback(
    (trayNumber?: number, assetNumber_?: string): CheckInSet => {
      const trayReference = trayNumber ?? checkInTraysBySet[setKitInstanceId].trays[checkInTrayId].trayReference;
      const assetNumber = generateTrayBarcode(
        deliveryId,
        trayNumber,
        assetNumber_ ?? checkInTraysBySet[setKitInstanceId].trays[checkInTrayId].assetNumber,
      );

      const updatedCheckInSet: CheckInSet = {
        ...checkInTraysBySet[setKitInstanceId],
        trays: {
          ...checkInTraysBySet[setKitInstanceId].trays,
          [checkInTrayId]: {
            ...checkInTraysBySet[setKitInstanceId].trays[checkInTrayId],
            trayReference,
            assetNumber,
            completed: true,
            allDevicesCleanOfDebris,
            allDevicesFunctional,
            allDevicesInTray,
            comments,
            missingPartsComment,
          },
        },
      };

      const updatedState: CheckInTraysBySet = {
        ...checkInTraysBySet,
        [setKitInstanceId]: updatedCheckInSet,
      };

      setCheckInTraysBySet(updatedState);

      return updatedCheckInSet;
    },
    [
      allDevicesCleanOfDebris,
      allDevicesFunctional,
      allDevicesInTray,
      checkInTrayId,
      checkInTraysBySet,
      comments,
      deliveryId,
      generateTrayBarcode,
      missingPartsComment,
      setCheckInTraysBySet,
      setKitInstanceId,
    ],
  );

  const onConfirmDecantTray = useCallback(() => {
    updateCheckInTraysBySet();
    navigate(
      Routes.CheckInTrayDecantImageCapture.path
        .replace(DELIVERY_ID_PARAM, deliveryId)
        .replace(SET_ID_PARAM, setKitInstanceId)
        .replace(CHECK_IN_TRAY_ID_PARAM, checkInTrayId),
    );
  }, [checkInTrayId, deliveryId, navigate, setKitInstanceId, updateCheckInTraysBySet]);

  const onConfirmTrayNumber = useCallback(
    async (trayNumber: number, assetNumber?: string) => {
      if (!delivery) {
        throw new Error('No delivery found');
      }

      try {
        try {
          // Validate tray number at the surgery level
          await validateTrayReference({ deliveryId, trayReference: trayNumber });
        } catch (error) {
          // We already show a toast error message in the global error handler so we just need to return here
          return;
        }

        // Safely get the previous setKitInstanceId or initialize it
        const previousSetState = checkInTraysBySet[setKitInstanceId] || {
          trays: {},
        };

        // Check if the tray number already exists in the set
        if (previousSetState.trays) {
          for (const trayId in previousSetState.trays) {
            const tray = previousSetState.trays[trayId];
            if (
              (tray.decantedTrays &&
                tray.decantedTrays.some(decantedTray => decantedTray.trayReference === trayNumber)) ||
              tray.trayReference === trayNumber
            ) {
              // do not let the user add the same tray number twice - show an error message and don't proceed with the save
              toast.error(lang.duplicateTrayNumberError);
              return;
            }
          }
        }

        setVisibleDialog(null);
        setIsSaving(true);

        const updatedCheckInSet = updateCheckInTraysBySet(trayNumber, assetNumber);

        await saveAndComplete({
          checkInSet: updatedCheckInSet,
          setKitInstanceId,
          checkInTrayId,
          setVisibleDialog,
          isDecant: false,
        });
      } catch (error) {
        console.warn(error);
      } finally {
        setIsSaving(false);
      }
    },
    [
      checkInTraysBySet,
      delivery,
      deliveryId,
      lang.duplicateTrayNumberError,
      saveAndComplete,
      setKitInstanceId,
      updateCheckInTraysBySet,
      validateTrayReference,
      checkInTrayId,
    ],
  );

  return (
    <>
      <Page>
        <Box>
          <PageHeader title={pageTitle} onBackClick={onBackClick}>
            <Button colors="active on white" onClick={onBackClick} endIcon={<CameraIcon />}>
              {lang.retakePhoto}
            </Button>

            <Button
              colors="white on active"
              loading={isSavingAndCompleting}
              disabled={isSavingAndCompleting}
              onClick={() => setVisibleDialog(deliverySet?.consolidatedSet ? 'TrayNumber' : 'ConfirmDecantTray')}
            >
              {lang.continue}
            </Button>
          </PageHeader>

          <CheckInTrayDetails
            delivery={delivery}
            allDevicesCleanOfDebris={allDevicesCleanOfDebris}
            setAllDevicesCleanOfDebris={setAllDevicesCleanOfDebris}
            allDevicesFunctional={allDevicesFunctional}
            setAllDevicesFunctional={setAllDevicesFunctional}
            allDevicesInTray={allDevicesInTray}
            setAllDevicesInTray={setAllDevicesInTray}
            comments={comments}
            setComments={setComments}
            setMissingPartsComment={updateMissingPartsComment}
          />
        </Box>
      </Page>

      {'ConfirmDecantTray' == visibleDialog && (
        <ConfirmDecantTrayDialog
          onConfirm={onConfirmDecantTray}
          onCancel={() => {
            setVisibleDialog('TrayNumber');
          }}
          onExit={() => setVisibleDialog(null)}
        />
      )}

      <CheckInTrayDialogs
        deliveryId={deliveryId || ''}
        orderRecordId={delivery?.orderRecordId || ''}
        noSets={!!delivery?.sets}
        visibleDialog={visibleDialog}
        setVisibleDialog={setVisibleDialog}
        onConfirm={onConfirmTrayNumber}
      />

      {(isSaving || isSavingAndCompleting) && <SavingPleaseWaitDialog reason={'saving'} />}
    </>
  );
};
